import { Injectable, inject } from "@angular/core";
import { NavigationEnd, NavigationError, Router } from "@angular/router";
import { Analytics, EventParams, logEvent } from 'firebase/analytics';

/**
 * 
 *  See EventNameString in analytics-public.d.ts
 * 
 'add_payment_info'
 'add_shipping_info'
 'add_to_cart'
 'add_to_wishlist'
 'begin_checkout'		-- After unsubmitted acc application first create, log with applicationId and maybe some details
 'checkout_progress'	
 'exception'			-- ERRORS
 'generate_lead'		-- When a lead_entrepreneur or lead_volunteer is created
 'login'				-- LOGIN
 'page_view'			-- ROUTER NAVIGATION
 'purchase'				-- After acc application is submitted (USE `applicationId:${applicationId}` for unique key)
 'refund'
 'remove_from_cart'
 'screen_view'			
 'search'
 'select_content'		-- Button and menu clicks
 'select_item'
 'select_promotion'
 'set_checkout_option'
 'share'
 'sign_up'			   -- Volunteer sign ups
 'timing_complete'
 'view_cart'
 'view_item'
 'view_item_list'
 'view_promotion'
 'view_search_results'
 */


/**
 * Service to log events to Google Analytics
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {

	private _router = inject(Router);
	private _analytics: Analytics | undefined = undefined;


	/**
	 * The AuthService initializes Firebase Analytics right after the
	 * Firebase App is created. It passes the Analytics object here to
	 * be used for recording events.
	 */
	setAnalytics(analytics: Analytics) {
		this._analytics = analytics;
	}


	constructor() {

		this._router.events.subscribe(event => {
			if (event instanceof NavigationEnd) this.logNavigationEnd(event);
			else if (event instanceof NavigationError) this.logNavigationError(event);
		});

		document.addEventListener("visibilitychange", () => {
			this.logUserVisibility(document.visibilityState);
		});
	}


	private logUserVisibility(state: 'hidden' | 'visible') {
		if (!this._analytics) return;
		if (state == 'hidden') logEvent(this._analytics, 'user_left', this.getPropertiesFromUrl());
		if (state == 'visible') logEvent(this._analytics, 'user_returned', this.getPropertiesFromUrl());
	}


	private logNavigationEnd(event: NavigationEnd) {

		if (!this._analytics) return;

		//
		// TODO: Determine if this should be manually logged or if GA4 is configured
		// to automatically log page views and referrers.  It's probably better to
		// use the automatic tracking, unless there is extra metadata to add.
		//

		const url = new URL(event.urlAfterRedirects, location.origin);

		logEvent(this._analytics, 'page_view', {
			page_title: url.pathname,
			page_location: event.urlAfterRedirects,
			page_path: event.url,
		});
	}


	private logNavigationError(event: NavigationError) {

		if (!this._analytics) return;

		this.logException({
			description: `Navigation Error`,
			fatal: true,
			error: event.error,
			url: event.url,
			target: event.target,
		});
	}


	logLogin(params: { email: string, provider: string }) {
		if (!this._analytics) return;
		logEvent(this._analytics, 'login', { ...params, ...this.getPropertiesFromUrl() });
	}

	logLogout(params: { email: string | undefined }) {
		if (!this._analytics) return;
		logEvent(this._analytics, 'logout', { ...params, ...this.getPropertiesFromUrl() });
	}


	logError(e: Error) {
		this.logException({
			description: `${e.name}: ${e.message}`,
			name: e.name,
			message: e.message,
			stack: e.stack,
			fatal: true,
		});
	}

	/**
	 * Log an exception event to Analytics.
	 * See the {@link https://developers.google.com/gtagjs/reference/ga4-events the GA4 reference documentation}.
	 */
	logException(params: {
		description: EventParams['description'];
		fatal: EventParams['fatal'];
		[key: string]: any;
	}) {

		if (!this._analytics) return;
		logEvent(this._analytics, 'exception', { ...params, ...this.getPropertiesFromUrl() });
	}


	/**
	 * Log when a button was clicked.
	 * @param activity - what kind of thing happened
	 * @param activityName - A human readable name that uniquely identifies this activity (e.g. the button English text or Course Name)
	 */
	logActivity(
		activity: 'button-click' | 'menu-click' | 'form-submission' | 'course-enrollment' | 'course-completion' | 'agreement-signed',
		activityName: string,
	) {
		if (!this._analytics) return;
		logEvent(this._analytics, activity, { 'name': activityName, ...this.getPropertiesFromUrl() });
	}


	logNewLead(
		lead: 'entrepreneur' | 'volunteer',
		params: { isStaff: boolean, campaign: string | undefined },

	) {
		if (!this._analytics) return;
		logEvent(this._analytics, lead + '_lead', { ...params, ...this.getPropertiesFromUrl() });
	}


	logNewUnsubmittedAccApplication(
		params: {
			accApplicationId: number,
			stage: 'Idea' | 'Existing',
			/** e.g. 'for profit' | 'nonprofit' */
			businessType: string,
			industry: string,
			/** Place address: e.g. street, city state zip  */
			location: string
		}
	) {
		if (!this._analytics) return;
		logEvent(this._analytics, 'acc_application_created', { ...params, ...this.getPropertiesFromUrl() });
	}



	logAccApplicationSubmitted(
		params: {
			accApplicationId: number,
			stage: 'Idea' | 'Existing',
			/** e.g. 'for profit' | 'nonprofit' */
			businessType: string,
			industry: string,
			/** Place address: e.g. street, city state zip  */
			location: string
		}
	) {
		if (!this._analytics) return;
		logEvent(this._analytics, 'acc_application_submitted', { ...params, ...this.getPropertiesFromUrl() });
	}


	logAccApplicationParticipantInviteAccepted(
		params: {
			accApplicationId: number,
			stage: 'Idea' | 'Existing',
			/** e.g. 'for profit' | 'nonprofit' */
			businessType: string,
			industry: string,
			/** Place address: e.g. street, city state zip  */
			location: string
		}
	) {
		if (!this._analytics) return;
		logEvent(this._analytics, 'acc_application_participant_joined', { ...params, ...this.getPropertiesFromUrl() });
	}



	logVolunteerSignup(
		params: {
			languageAvailability: 'en' | 'es' | 'both',
			trimester: string,
			volunteerType: 'mentor' | 'specialist' | 'office hours',
		}
	) {
		if (!this._analytics) return;
		logEvent(this._analytics, 'volunteer_signup', { ...params, ...this.getPropertiesFromUrl() });
	}



	private getPropertiesFromUrl(): {
		language: 'en' | 'es' | undefined,
		role: 'entrepreneur' | 'volunteer' | undefined,
		url: string,
	} {

		const url = window.location.href.split('?')[0] ?? '';

		let language: 'en' | 'es' | undefined = undefined;
		if (url.includes('/en/') || url.endsWith('/en')) language = 'en';
		if (url.includes('/es/') || url.endsWith('/es')) language = 'es';

		let role: 'entrepreneur' | 'volunteer' | undefined = undefined;
		if (url.includes('/entrepreneur/') || url.endsWith('/entrepreneur')) role = 'entrepreneur';
		if (url.includes('/volunteer/') || url.endsWith('/volunteer')) role = 'volunteer';

		return { language, role, url };
	}

}